import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import OrderDialog from '../../components/OrderDialog';
import { formatDate } from '../../utils/formatDate';
import { getServerUrl } from '../../utils/serverUrl';
import SweetTreatDialog from '../../components/SweetTreatDialog';


/**
 * AllOrders.jsx
 * ────────────────────────────────────────────────────────────────
 * Uses `orders`, `setOrders`, and `fetchOrders` supplied by AdminLayout.
 */

export default function AllOrders() {
  const { orders, setOrders, fetchOrders, ordersLoading } = useOutletContext();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // NEW: event date range + sort order
  const [fromDate, setFromDate] = useState(''); // YYYY-MM-DD
  const [toDate, setToDate] = useState('');     // YYYY-MM-DD
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc' — default: newest first
  const [sortBy, setSortBy] = useState('eventDate'); // 'orderId' | 'eventDate' — default: Order ID
  const [typeFilter, setTypeFilter] = useState(''); // '' = all | standard | custom | cupcakes | sweet

  const SERVER_URL = getServerUrl();

  /* Ensure we have data */
  useEffect(() => {
    if (orders.length === 0 && !ordersLoading) fetchOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Action handlers (reuse server URL) */
  const handleAccept = async (id) => {
    const { data } = await axios.put(`${SERVER_URL}/api/orders/${id}/accept`);
    setOrders(prev => prev.map(o => (o._id === id ? data : o)));
    setSelectedOrder(prev => (prev?._id === id ? data : prev));
  };
  const handleReject = async (id) => {
    const { data } = await axios.put(`${SERVER_URL}/api/orders/${id}/reject`);
    setOrders(prev => prev.map(o => (o._id === id ? data : o)));
    setSelectedOrder(prev => (prev?._id === id ? data : prev));
  };
  const handleUpdateStatus = async (id, status) => {
    await axios.put(`${SERVER_URL}/api/orders/${id}/update-status`, { status });
    setOrders(prev => prev.map(o => (o._id === id ? { ...o, status } : o)));
    setSelectedOrder(prev => (prev?._id === id ? { ...prev, status } : prev));
  };

  /* Helpers */
  const inDateRange = (eventDateStr) => {
    if (!fromDate && !toDate) return true;
    const ev = new Date(eventDateStr);
    if (Number.isNaN(ev.getTime())) return false;

    if (fromDate) {
      const from = new Date(fromDate);
      // normalize to start of the day
      from.setHours(0, 0, 0, 0);
      if (ev < from) return false;
    }

    if (toDate) {
      const to = new Date(toDate);
      // include the entire end day
      to.setHours(23, 59, 59, 999);
      if (ev > to) return false;
    }

    return true;
  };

  const getOrderTypeKey = (order) => {
    if (order.orderType === 'sweet') return 'sweet';
    if (order.cakeShape === 'Cupcakes') return 'cupcakes';
    if (!order.quoteRequest) return 'standard';
    return 'custom';
  };

  /* Search + date + type filtering */
  const filteredOrders = orders.filter(o => {
    const fullName = `${o.firstName} ${o.lastName}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
      o.orderId?.toString().includes(term) ||
      fullName.includes(term) ||
      o.customerPhone?.toLowerCase().includes(term) ||
      o.customerEmail?.toLowerCase().includes(term)
    );

    const matchesDate = inDateRange(o.eventDate);
    const matchesType = !typeFilter || getOrderTypeKey(o) === typeFilter;

    return matchesSearch && matchesDate && matchesType;
  });

  /* Sort by event date */
  const filteredSortedOrders = [...filteredOrders].sort((a, b) => {
    const getComparable = (o) => {
      if (sortBy === 'eventDate') {
        const t = new Date(o.eventDate).getTime();
        return Number.isNaN(t) ? -Infinity : t;
      }
      // Default: orderId
      const n = Number(o.orderId);
      return Number.isNaN(n) ? String(o.orderId ?? '') : n;
    };

    const va = getComparable(a);
    const vb = getComparable(b);

    if (va < vb) return sortOrder === 'asc' ? -1 : 1;
    if (va > vb) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setTypeFilter('');
    setSortBy('orderId');
    setSortOrder('desc');
  };

  
const getPaymentStatus = (order) => {
  // --- DATE CUTOFF LOGIC ---
  const cutoff = new Date("2025-11-19T00:00:00");  
  const createdAt = new Date(order.createdAt);

  if (createdAt < cutoff) {
    return (
      <span className="text-gray-500 italic">
        Legacy Order
      </span>
    );
  }

  // --- NORMAL PAYMENT LOGIC ---
  const price = Number(order.price || 0);
  const paid = Number(order.amountPaid || 0);
  const remaining = price - paid;

  if (paid <= 0)
    return <span className="text-red-600 font-semibold">Due ${price.toFixed(2)}</span>;

  if (paid > 0 && paid < price)
    return (
      <span className="text-yellow-600 font-semibold">
        Due – ${remaining.toFixed(2)}
      </span>
    );

  return <span className="text-green-600 font-semibold">Fully Paid</span>;
};

const getOrderTypeMeta = (order) => {
  if (order.orderType === 'sweet') {
    return { label: 'Sweet Treats', classes: 'bg-pink-100 text-pink-700' };
  }
  if (order.cakeShape === 'Cupcakes') {
    return { label: 'Cupcakes', classes: 'bg-amber-100 text-amber-700' };
  }
  if (!order.quoteRequest) {
    return { label: 'Standard Cake', classes: 'bg-blue-100 text-blue-700' };
  }
  return { label: 'Custom Cake', classes: 'bg-purple-100 text-purple-700' };
};



  /* Render */
  return (
    <div className="p-6 bg-candy-background">
      <h2 className="text-2xl font-bold text-candy-primary mb-6">All Orders</h2>

      {/* Container: search left, filters right */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">

        {/* Search input (left side) */}
        <input
          type="text"
          placeholder="Search by Order ID, Name, Phone, Email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full lg:max-w-md p-3 border border-candy-border rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-candy-primary"
        />

        {/* Filters (right side) */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-candy-primary mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="p-3 border border-candy-border rounded-md text-sm shadow-sm bg-white min-w-[140px] focus:outline-none focus:ring-2 focus:ring-candy-primary"
            >
              <option value="">All types</option>
              <option value="standard">Standard Cake</option>
              <option value="custom">Custom Cake</option>
              <option value="cupcakes">Cupcakes</option>
              <option value="sweet">Sweet Treats</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-candy-primary mb-1">From pickup date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="p-3 border border-candy-border rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-candy-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-candy-primary mb-1">To pickup date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="p-3 border border-candy-border rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-candy-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-candy-primary mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-3 border border-candy-border rounded-md text-sm shadow-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary"
            >
              <option value="orderId">Order ID</option>
              <option value="eventDate">Pickup Date</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-candy-primary mb-1">Sort direction</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="p-3 border border-candy-border rounded-md text-sm shadow-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary"
            >
              <option value="desc">High → Low / Newest</option>
              <option value="asc">Low → High / Oldest</option>
            </select>
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-2 rounded-md text-sm font-semibold bg-white border border-candy-border shadow-sm hover:bg-candy-background transition"
          >
            Clear filters
          </button>
        </div>
      </div>


      {ordersLoading ? (
        <div className="flex justify-center items-center py-20">
          <svg className="animate-spin h-8 w-8 text-candy-primary" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
            <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
          </svg>
          <span className="ml-3 text-candy-primary font-semibold text-lg">Loading orders…</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse bg-white shadow-md rounded-xl overflow-hidden text-sm">
            <thead className="bg-candy-primary text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Order ID</th>
                <th className="px-4 py-3 text-left font-semibold">Last Name</th>
                <th className="px-4 py-3 text-left font-semibold">First Name</th>
                <th className="px-4 py-3 text-left font-semibold">Pickup Date</th>
                <th className="px-4 py-3 text-left font-semibold">Phone</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>

                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Deco Price</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-left font-semibold">Payment</th>

              </tr>
            </thead>
            <tbody>
              {filteredSortedOrders.map(o => {
                const typeMeta = getOrderTypeMeta(o);
                return (
                <tr
                  key={o._id}
                  className={`border-b hover:bg-candy-background cursor-pointer transition ${o.status === 'Open' ? 'bg-green-300' : ''}`}
                  onClick={() => setSelectedOrder(o)}
                >
                  <td className="px-4 py-2">{o.orderId}</td>
                  <td className="px-4 py-2">{o.lastName}</td>
                  <td className="px-4 py-2">{o.firstName}</td>
                  <td className="px-4 py-2">{formatDate(o.eventDate)}</td>
                  <td className="px-4 py-2">{o.customerPhone}</td>
                  {/* New Type column */}
                  <td className="px-4 py-2">
                    <span
                      className={
                        `px-2 py-1 rounded-full text-xs font-semibold ${typeMeta.classes}`
                      }
                    >
                      {typeMeta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2">{o.status}</td>
                  <td className="px-4 py-2">${o.decorations_price || 0}</td>
                  <td className="px-4 py-2">${o.price}</td>
                  <td className="px-4 py-2">{getPaymentStatus(o)}</td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {selectedOrder && (
        selectedOrder.orderType === 'sweet' ? (
          <SweetTreatDialog
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onAccept={handleAccept}
            onReject={handleReject}
            onUpdateStatus={handleUpdateStatus}
          />
        ) : (
          <OrderDialog
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onAccept={handleAccept}
            onReject={handleReject}
            onUpdateStatus={handleUpdateStatus}
          />
        )
      )}
    </div>
  );
}
